import { Line } from "@ant-design/plots";
import { Button, Card, Col, DatePicker, Form, Row, Space, Spin, Table, Typography } from "antd";
import { getData } from "apiServices/common";
import ListTable from "common/ListTable";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { formatNumberWithCommas } from "utils/functions";
import DateRangePresets from "common/SearchForm/DateRangePresets";

const columns = [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
  },
  {
    title: "Projected CM/Working Day (Taka)",
    dataIndex: "projected_expense_taka",
    key: "projected_expense_taka",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Actual CM/Working Day (Taka)",
    dataIndex: "actual_cm_taka",
    key: "actual_cm_taka",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Actual Cost/Working Day (Taka)",
    dataIndex: "actual_cost_taka",
    key: "actual_cost_taka",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Projected Cost (Taka)",
    dataIndex: "projected_cost_taka",
    key: "projected_cost_taka",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Projected Operating Profit/(Loss) TK",
    dataIndex: "projected_operting_profit_loss_taka",
    key: "projected_operting_profit_loss_taka",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Actual Operating Profit/(Loss) TK",
    dataIndex: "actual_operting_profit_loss_taka",
    key: "actual_operting_profit_loss_taka",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Variance Between Budgeted & Actual Profit",
    dataIndex: "varriance_between_budgeted_actual_profit",
    key: "varriance_between_budgeted_actual_profit",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Cumulative Projected Operating Profit/Loss TK",
    dataIndex: "cum_proj_operating_profit_loss",
    key: "cum_proj_operating_profit_loss",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
  {
    title: "Cumulative Operating Profit/Loss TK",
    dataIndex: "cum_operating_profit_loss",
    key: "cum_operating_profit_loss",
    align: "right",
    render: (value) => formatNumberWithCommas(parseFloat(value === "" || value === undefined ? 0 : value).toFixed(2)),
  },
];

const DailyProfitLossSummary = () => {
  const [form] = Form.useForm();
  const startDateWatch = Form.useWatch("start_date", form) || moment();
  const endDateWatch = Form.useWatch("end_date", form) || moment();
  const [tableData, setTableData] = useState([]);
  const [barChartData, setBarChartData] = useState([]);
  const [cumulativeChartData, setCumulativeChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Final fix wave (I1): distinguish "the window is genuinely empty" from "the request
  // failed/timed out" — getDataForReport swallows its error into a sentinel so the caller
  // can tell the two apart and never show the "Widen the range" hint for a real failure.
  const [reportError, setReportError] = useState(false);

  // Fix round 1: guard against setState after unmount (this endpoint is slow
  // enough — see NOTE.md — that a user can plausibly navigate away before a
  // manually-triggered Search resolves).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Round 2 final wave: request-sequence ref — now that auto-fetch runs on mount again,
  // a fast manual Search must never be overwritten by the slower mount-time fetch
  // resolving afterwards (or vice versa).
  const reqIdRef = useRef(0);

  const config = {
    data: barChartData,
    xField: "date",
    yField: "actual_operting_profit_loss_taka",
    point: {
      shapeField: "square",
      sizeField: 4,
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    style: {
      lineWidth: 2,
    },
  };

  const cumulativeConfig = {
    ...config,
    data: cumulativeChartData,
    yField: "cumulative_value",
    tooltip: {
      showTitle: true,
      title: "date",
      fields: ["cumulative_value"],
      formatter: (datum) => ({
        name: "Cumulative Operating Profit/(Loss) TK",
        value: formatNumberWithCommas(Number(datum.cumulative_value || 0).toFixed(2)),
      }),
    },
  };

  // Calculate cumulative data from daily values
  const calculateCumulativeData = (data) => {
    let cumulativeSum = 0;
    return data.map((item) => {
      cumulativeSum += Number(item.actual_operting_profit_loss_taka) || 0;
      return {
        date: item.date,
        cumulative_value: cumulativeSum,
      };
    });
  };

  const onFilter = async (values) => {
    const startDateOfCurrentMonth = moment().startOf("month");
    const endDateOfCurrentMonth = moment();
    const { start_date, end_date } = values;
    const startDate = start_date
      ? moment(start_date).format("YYYY-MM-DD")
      : startDateOfCurrentMonth.format("YYYY-MM-DD");
    const endDate = end_date ? moment(end_date).format("YYYY-MM-DD") : endDateOfCurrentMonth.format("YYYY-MM-DD");

    // Round 2 final wave: claim this request's sequence number before the await so a
    // later, faster request (the mount-time auto-fetch racing a quick manual Search, or
    // the reverse) can invalidate this one.
    const myReq = ++reqIdRef.current;

    try {
      setLoading(true);
      setHasSearched(true);
      const data = await getDataForReport(startDate, endDate);

      if (!mountedRef.current || myReq !== reqIdRef.current) return;

      // Final fix wave (I1): null means the request failed/timed out; [] means the window
      // is genuinely empty. Only the latter gets the "Widen the range" hint.
      if (data === null) {
        setReportError(true);
        setTableData([]);
        setBarChartData([]);
        setCumulativeChartData([]);
        return;
      }
      setReportError(false);
      setTableData(data);

      // Set daily chart data
      const dailyData = data.map((item) => ({
        date: item.date,
        actual_operting_profit_loss_taka: Number(item.actual_operting_profit_loss_taka) || 0,
      }));
      setBarChartData(dailyData);

      // Set cumulative chart data
      setCumulativeChartData(calculateCumulativeData(data));
    } catch (error) {
      console.log(error);
    } finally {
      if (mountedRef.current && myReq === reqIdRef.current) setLoading(false);
    }
  };

  const handleReset = () => {
    setTableData([]);
    setBarChartData([]);
    setCumulativeChartData([]);
    setLoading(false);
    setHasSearched(false);
    form.setFieldsValue({
      start_date: moment(),
      end_date: moment(),
    });
  };

  useEffect(() => {
    // Round 2 final wave: restore the mount-time fetch. Round 1 removed it because this
    // endpoint reliably exceeded the backend's 60s execution limit; the set-based rewrite
    // (REVIEW-C2 §5) now answers the seeded current-month default in ~1-4s even under
    // load, a 5x+ margin to the 60s fatal, so auto-fetching this specific pre-seeded
    // window is safe again. Seed the form with the same current-month default onFilter's
    // own fallback (L135-141) computes, then submit through the same onFinish={onFilter}
    // path the Search button uses — no duplicate fetch logic. The mounted-ref and
    // request-sequence guards above cover a manual Search/Reset racing this fetch.
    const defaultStartDate = moment().startOf("month");
    const defaultEndDate = moment();
    form.setFieldsValue({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
    });
    form.submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDataForReport = async (firstDate, lastDate) => {
    try {
      const query = `/api/pnl-report/daily-profit-loss-summery?start_date=${firstDate}&end_date=${lastDate}`;
      const res = await getData(query);
      // Use res.data?.data directly
      return res.data?.data || [];
    } catch (error) {
      console.log(error);
      // Final fix wave (I1): null is the failure sentinel — distinct from a genuinely
      // empty [] — so the caller can render an error state instead of "Widen the range".
      return null;
    }
  };

  return (
    <Card
      title={`Daily Profit & Loss Summary: ${
        startDateWatch && endDateWatch
          ? `(${moment(startDateWatch).format("DD-MMM-YY")} to ${moment(endDateWatch).format("DD-MMM-YY")})`
          : ""
      }`}
    >
      <Spin spinning={loading} tip="Loading data...">
        <Row>
          <Col span={24}>
            <Form layout="vertical" form={form} onFinish={onFilter} onReset={handleReset}>
              <Row gutter={24} align={"middle"}>
                <Col span={24}>
                  <DateRangePresets form={form} startName="start_date" endName="end_date" />
                </Col>
                <Col span={3}>
                  <Form.Item
                    label="Start Date"
                    name="start_date"
                    rules={[
                      {
                        required: true,
                        message: "Please select start date",
                      },
                      {
                        validator: async (_, value) => {
                          if (value && value > endDateWatch) {
                            return Promise.reject(new Error("Start date must be less than today"));
                          }
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      allowClear={false}
                      disabledDate={(current) => {
                        if (!current) {
                          return false;
                        }

                        return current > moment();
                      }}
                      // showTime={{ format: "DD-MMM-YY" }}
                      format="DD-MMM-YY"
                      style={{ width: "100%" }}
                      defaultValue={moment()}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item
                    label="End Date"
                    name="end_date"
                    rules={[
                      {
                        required: true,
                        message: "Please select start date",
                      },
                      {
                        validator: async (_, value) => {
                          if (value && value < startDateWatch) {
                            return Promise.reject(new Error("End date must be greater than start date"));
                          }
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      allowClear={false}
                      disabledDate={(current) => {
                        if (!current) {
                          return false;
                        }
                        const endOfMonth = moment(startDateWatch).endOf("month");
                        const startOfMonth = moment(startDateWatch).startOf("month");
                        return current >= endOfMonth || current <= startOfMonth;
                      }}
                      // showTime={{ format: "DD-MMM-YY" }}
                      format="DD-MMM-YY"
                      defaultValue={moment()}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="reset"
                      danger
                      loading={loading}
                      disabled={loading}
                      style={{
                        marginTop: "10px",
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      disabled={loading}
                      style={{
                        marginTop: "10px",
                      }}
                    >
                      Search
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </Col>
          {hasSearched && (
            <>
              <Col span={24}>
                <ListTable
                  columns={columns}
                  dataSource={tableData}
                  pagination={false}
                  bordered
                  loading={loading}
                  error={
                    reportError
                      ? "The report could not be loaded (timed out). Try a shorter range or press Search again."
                      : null
                  }
                  onRetry={() => form.submit()}
                  rowKey={(record, idx) => idx}
                  scroll={{
                    x: 1200,
                    y: 500,
                  }}
                  sticky
                  className="compact-table"
                  locale={{
                    // Final fix wave (I1): a failed/timed-out request must never be
                    // rendered as an empty-window hint — that tells the user to do the
                    // one thing (widen the range) that makes a timeout certain.
                    emptyText: reportError
                      ? "The report could not be loaded (timed out). Try a shorter range or press Search again."
                      : `No records between ${moment(startDateWatch).format(
                          "DD-MMM-YYYY",
                        )} and ${moment(endDateWatch).format("DD-MMM-YYYY")}. Widen the range above.`,
                  }}
                  summary={(data) => {
                    let totalProjectedCM = 0;
                    let totalActualCM = 0;
                    let totalActualCost = 0;
                    let totalProjectedCost = 0;
                    let totalProjectedProfit = 0;
                    let totalActualProfit = 0;
                    let totalVariance = 0;
                    let totalCumProjectedProfit = 0;
                    let totalCumActualProfit = 0;
                    data.forEach(
                      ({
                        projected_expense_taka,
                        actual_cm_taka,
                        actual_cost_taka,
                        projected_cost_taka,
                        projected_operting_profit_loss_taka,
                        actual_operting_profit_loss_taka,
                        varriance_between_budgeted_actual_profit,
                        cum_proj_operating_profit_loss,
                        cum_operating_profit_loss,
                      }) => {
                        totalProjectedCM += parseFloat(projected_expense_taka) || 0;
                        totalActualCM += parseFloat(actual_cm_taka) || 0;
                        totalActualCost += parseFloat(actual_cost_taka) || 0;
                        totalProjectedCost += parseFloat(projected_cost_taka) || 0;
                        totalProjectedProfit += parseFloat(projected_operting_profit_loss_taka) || 0;
                        totalActualProfit += parseFloat(actual_operting_profit_loss_taka) || 0;
                        totalVariance += parseFloat(varriance_between_budgeted_actual_profit) || 0;
                        totalCumProjectedProfit += parseFloat(cum_proj_operating_profit_loss) || 0;
                        totalCumActualProfit += parseFloat(cum_operating_profit_loss) || 0;
                      },
                    );

                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Typography.Text strong>Total</Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalProjectedCM.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <Typography.Text strong>{formatNumberWithCommas(totalActualCM.toFixed(2))}</Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalActualCost.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalProjectedCost.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalProjectedProfit.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalActualProfit.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            <Typography.Text strong>{formatNumberWithCommas(totalVariance.toFixed(2))}</Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={8} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalCumProjectedProfit.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={9} align="right">
                            <Typography.Text strong>
                              {formatNumberWithCommas(totalCumActualProfit.toFixed(2))}
                            </Typography.Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                />
              </Col>
              <Col span={24}>
                <Typography.Text strong>Daily Profit & Loss Summary of CGL (Projected vs Actual)</Typography.Text>
                <Line {...config} />
                <br />
              </Col>
              <Col span={24}>
                <Typography.Text strong>Cumulative Profit & Loss Summary of CGL (Projected vs Actual)</Typography.Text>
                <Line {...cumulativeConfig} />
              </Col>
            </>
          )}
        </Row>
      </Spin>
    </Card>
  );
};

export default DailyProfitLossSummary;
